import { useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { safeDateMs } from "../../../sortLogic";
import { MediaPreviewCard } from "../../components/MediaPreviewCard";
import type { MediaItem } from "../../../types";

const PREVIEW_COUNT = 6;

// Portals into #homeMemoriesView - a small teaser of the most recent media
// with a "See all" link to the full /gallery. Deliberately just a preview:
// no search/sort/select/favorite controls here, those stay exclusively on
// /gallery per the approved design.
export function RecentMemories() {
  const media = useAppState((s) => s.media);
  const navigate = useNavigate();

  // appState.media's order reflects whatever the gallery's own sort
  // toolbar last chose (sortMediaInPlace) - NOT recency. This sorts an
  // independent copy by safeDateMs so this preview is unaffected by that.
  const recent = useMemo(() => [...media].sort((a, b) => safeDateMs(b) - safeDateMs(a)).slice(0, PREVIEW_COUNT), [media]);

  const anchor = getVanillaEl<HTMLDivElement>("homeMemoriesView");
  if (!anchor) return null;

  function onView(item: MediaItem): void {
    // Navigating the Lightbox needs the item's index in the ACTUAL
    // appState.media array (whatever order that's currently in), not its
    // position in this component's own recency-sorted copy.
    const idx = media.findIndex((m) => m.filename === item.filename);
    if (idx === -1) return;
    appState.currentMediaIndex = idx;
    notifyStateChange();
  }

  return createPortal(
    <>
      <div className="home-section-heading">
        <h2 className="home-section-title">Our Memories</h2>
        <button type="button" className="home-see-all" onClick={() => navigate("/gallery")}>
          See all <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
      </div>

      {!recent.length ? (
        <p className="small muted mb-0">No memories yet - upload your first photo or video to get started.</p>
      ) : (
        // Up to 6 render here, but a narrow viewport only shows the first 4
        // (2x2) via CSS - Home stays a teaser, not a second gallery; "See
        // all" is always visible for the rest.
        <div className="recent-memories-grid">
          {recent.map((item) => (
            <MediaPreviewCard key={item.filename} item={item} onView={() => onView(item)} />
          ))}
        </div>
      )}
    </>,
    anchor,
  );
}
